import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ScreenType } from 'src/app/enums/enums';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NavigationService } from 'src/app/services/navigation.service';
import { ShippingCourierFormData } from 'src/app/models/shipping-courier/shipping-courier-form-data.model';
import {
  emailValidator,
  futureDateValidator,
  pastDateValidator,
  phoneValidator,
} from 'src/app/common/validatos/validators';
import { FormService } from 'src/app/services/form.service';
import { ErrorResponse } from 'src/app/models/error/error-response.model';
import { ErrorMsg } from 'src/app/models/error/error-msg.model';
import { ApiService } from 'src/app/services/api.service';
import { firstValueFrom } from 'rxjs';
import { SubmitButtonComponent } from '../../../common/submit-button/submit-button.component';
import { BackButtonComponent } from '../../../common/back-button/back-button.component';
import { FormInputControlComponent } from '../../../common/form-input-control/form-input-control.component';
import { FormHeaderComponent } from '../../../common/form-header/form-header.component';
import { HttpErrorResponse } from '@angular/common/http';
import { CaptchaDialogService } from 'src/app/services/captcha-dialog.service';
import { FormErrorMessageComponent } from '../../../common/form-error-message/form-error-message.component';
import { StoreService } from 'src/app/services/store.service';

@Component({
  selector: 'app-shipping-courier-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SubmitButtonComponent,
    BackButtonComponent,
    FormInputControlComponent,
    FormHeaderComponent,
    FormErrorMessageComponent,
  ],
  templateUrl: './shipping-courier-form.component.html',
  styleUrl: './shipping-courier-form.component.scss',
})
export class ShippingCourierFormComponent implements AfterViewInit {
  ScreenType = ScreenType;

  courierForm!: FormGroup;
  isSubmitLoading = signal(false);
  submitException = signal(false);

  disableFetchedFields = false;

  constructor(
    private navigationService: NavigationService,
    private fb: FormBuilder,
    public formService: FormService,
    private apiService: ApiService,
    private cdr: ChangeDetectorRef,
    private captchaDialogService: CaptchaDialogService,
    protected storeService: StoreService
  ) {
    if (this.storeService.getSender()) {
      this.disableFetchedFields = true;
    }

    this.initializeForm();
  }

  isPayerNoProvided(): boolean {
    return this.storeService.getPayerNo() ? true : false;
  }

  get name() {
    return this.courierForm.get('name');
  }
  get company() {
    return this.courierForm.get('company');
  }
  get zipCode() {
    return this.courierForm.get('zip_code');
  }
  get city() {
    return this.courierForm.get('city');
  }
  get street() {
    return this.courierForm.get('street');
  }
  get houseNumber() {
    return this.courierForm.get('house_number');
  }
  get apartmentNumber() {
    return this.courierForm.get('apartment_number');
  }
  get phone() {
    return this.courierForm.get('phone');
  }
  get email() {
    return this.courierForm.get('email');
  }
  get labelEmail() {
    return this.courierForm.get('label-email');
  }
  get date() {
    return this.courierForm.get('date');
  }
  get instructions() {
    return this.courierForm.get('instructions');
  }

  private initializeFormControl(
    valueFromData: string | number | undefined | null,
    valueFromResponse: string | number | undefined | null,
    validators: any[] = [],
    disabled = false
  ) {
    return this.fb.control(
      { value: valueFromData ?? valueFromResponse, disabled },
      validators
    );
  }

  private initializeForm() {
    const sender = this.storeService.getSender();
    const formData = this.storeService.getShippingCourierFormData();

    this.courierForm = this.fb.group({
      name: this.initializeFormControl(
        formData?.name,
        sender?.name,
        [Validators.required],
        this.disableFetchedFields
      ),
      company: this.initializeFormControl(
        formData?.company,
        sender?.companyName,
        [],
        this.disableFetchedFields
      ),
      zip_code: this.initializeFormControl(
        formData?.zipCode,
        sender?.postalCode,
        [Validators.required, Validators.pattern('^[0-9]{2}-[0-9]{3}$')],
        this.disableFetchedFields
      ),
      city: this.initializeFormControl(
        formData?.city,
        sender?.city,
        [Validators.required],
        this.disableFetchedFields
      ),
      street: this.initializeFormControl(
        formData?.street,
        this.parseAddress(sender?.address1)[0],
        [Validators.required],
        this.disableFetchedFields
      ),
      house_number: this.initializeFormControl(
        formData?.houseNumber,
        this.parseAddress(sender?.address1)[1],
        [Validators.required],
        this.disableFetchedFields
      ),
      apartment_number: this.initializeFormControl(
        formData?.apartmentNumber,
        this.parseAddress(sender?.address1)[2],
        [],
        this.disableFetchedFields
      ),
      phone: this.initializeFormControl(formData?.phone, sender?.phone, [
        Validators.required,
        phoneValidator(),
      ]),
      email: this.initializeFormControl(formData?.email, sender?.email, [
        Validators.required,
        emailValidator(),
      ]),
      date: this.initializeFormControl(formData?.date, this.getTodayDate(), [
        Validators.required,
        pastDateValidator(),
        futureDateValidator(),
      ]),
      instructions: this.initializeFormControl(formData?.instructions, '', []),
    });

    if (!this.storeService.getSender()) {
      this.courierForm.addControl(
        'label-email',
        this.initializeFormControl(formData?.emailForLabel, undefined, [
          emailValidator(),
        ])
      );
    }

    this.formService.markAllNotEmptyAsTouched(this.courierForm);
    this.courierForm.valueChanges.subscribe(() => this.saveFormDataToCache());
    this.zipCode?.valueChanges.subscribe(() => {
      this.city?.updateValueAndValidity({ emitEvent: false });
    });

    this.city?.valueChanges.subscribe(() => {
      this.zipCode?.updateValueAndValidity({ emitEvent: false });
    });
  }

  ngAfterViewInit() {
    const errorResponse = this.storeService.getErrorResponse();
    let errorMsgCopy: ErrorMsg[] | undefined = undefined;
    if (errorResponse) {
      errorMsgCopy = errorResponse?.errorMsg?.filter((err) => {
        let control: AbstractControl | undefined | null;
        if (err.field === 'name' && err.idRow === 1) {
          control = this.name;
        } else if (err.field === 'companyName' && err.idRow === 1) {
          control = this.company;
        } else if (err.field === 'city' && err.idRow === 1) {
          control = this.city;
        } else if (err.field === 'postalCode' && err.idRow === 1) {
          control = this.zipCode;
        } else if (err.field === 'address1' && err.idRow === 1) {
          control = this.street;
        } else if (err.field === 'phone' && err.idRow === 1) {
          control = this.phone;
        } else if (err.field === 'email' && err.idRow === 1) {
          control = this.email;
        } else if (err.field === 'pckDate') {
          control = this.date;
        } else if (err.field === 'pckInfo') {
          control = this.instructions;
        } else if (err.field === 'pickupDate') {
          control = this.phone;
        } else if (err.field === 'emailForLabel') {
          control = this.labelEmail;
        }

        if (control) {
          control?.setErrors({ [err.errorCode]: true });
          control?.markAsTouched();
          return false;
        }

        return true;
      });
    }

    const errorRespCpy: ErrorResponse = {
      httpStatus: errorResponse?.httpStatus,
      errorMsg: errorMsgCopy,
    };
    this.storeService.setPickupErrorResponse(errorRespCpy);
    this.cdr.detectChanges();
  }

  onBackButtonAction() {
    this.storeService.setSelectedPartners([]);
    this.navigationService.changeSposobNadaniaStepNumberTo(0);
    if (this.isPayerNoProvided()) {
      this.navigationService.changeScreenTo(ScreenType.OPCJE_DORECZENIA);
    } else {
      this.navigationService.changeScreenTo(ScreenType.STRONA_GLOWNA);
    }
  }

  async onSubmit() {
    this.isSubmitLoading.set(true);
    this.storeService.setShippingPointFormData(undefined);

    try {
      if (this.courierForm.invalid) {
        this.formService.markAllAsTouched(this.courierForm);
        this.submitException.set(true);
        return;
      }

      this.saveFormDataToCache();

      try {
        const cityZipCodeResponse = await firstValueFrom(
          this.apiService.submitCityZipCodeRequest({
            zipCode: this.zipCode?.value,
            city: this.city?.value,
          })
        );
      } catch (error) {
        this.handleCityZipCodeError(error);
        return;
      }

      const paletteAmount =
        this.storeService.getShipmentFormData()?.palettes?.length ?? 0;

      try {
        const pickupDateHoursPeriodResponse = await firstValueFrom(
          this.apiService.getPickupDateHoursPeriod(
            this.zipCode?.value,
            this.date?.value,
            paletteAmount
          )
        );

        this.storeService.setPickupDateHoursPeriodResponse(
          pickupDateHoursPeriodResponse
        );

        this.submitException.set(false);
        this.storeService.setSelectedPartners([]);
        this.navigationService.changeSposobNadaniaStepNumberTo(1);
      } catch (error) {
        this.handlePickupError(error as HttpErrorResponse);
      }
    } finally {
      this.isSubmitLoading.set(false);
    }
  }

  handleCityZipCodeError(error: any) {
    try {
      const errorResponse: ErrorResponse = {
        httpStatus: error.status,
        errorMsg: error.error.errorMsg.map((msg: any) => ({
          errorCode: msg.errorCode,
          field: msg.field,
          idRow: msg.idRow,
          errorMsgLabel: msg.errorMsgLabel,
          errorMsgArgs: msg.errorMsgArgs,
        })),
      };
      if (errorResponse?.errorMsg?.[0]?.errorCode === 'FORBIDDEN') {
        this.captchaDialogService.openCaptchaDialog();
      } else if (errorResponse?.errorMsg?.[0]?.errorCode === 'INCONSISTENT') {
        this.zipCode?.setErrors({ zip_code_city_inconsistent: true });
        this.city?.setErrors({ zip_code_city_inconsistent: true });
      } else {
        this.submitException.set(true);
      }
    } catch (error) {
      this.submitException.set(true);
    }
  }

  handlePickupError(error: HttpErrorResponse) {
    try {
      const errorResponse: ErrorResponse = {
        httpStatus: error.status,
        errorMsg: error.error.errorMsg.map((msg: any) => ({
          errorCode: msg.errorCode,
          field: msg.field,
          idRow: msg.idRow,
          errorMsgLabel: msg.errorMsgLabel,
          errorMsgArgs: msg.errorMsgArgs,
        })),
      };

      if (errorResponse && errorResponse.errorMsg) {
        errorResponse.errorMsg.forEach((err: any) => {
          if (err.errorCode === 'FORBIDDEN') {
            this.captchaDialogService.openCaptchaDialog();
          } else if (err.errorMsgLabel === 'api.err.msg.date.unavailable') {
            this.date?.setErrors({ DATE_UNAVAILABLE: true });
            this.date?.markAsTouched();
          } else if (err.errorMsgLabel === 'api.err.msg.incorrect.format') {
            this.zipCode?.setErrors({ invalid: true });
            this.zipCode?.markAsTouched();
          } else {
            this.submitException.set(true);
          }
        });
      } else {
        this.submitException.set(true);
      }
    } catch (error) {
      this.submitException.set(true);
    }
  }

  saveFormDataToCache(): void {
    const shippingCourierFormData: ShippingCourierFormData = {
      name: this.name?.value,
      company: this.company?.value,
      zipCode: this.zipCode?.value,
      city: this.city?.value,
      street: this.street?.value,
      houseNumber: this.houseNumber?.value,
      apartmentNumber: this.apartmentNumber?.value,
      phone: this.phone?.value,
      email: this.email?.value,
      date: this.date?.value,
      instructions: this.instructions?.value,
      emailForLabel: this.labelEmail?.value,
    };

    this.storeService.setShippingCourierFormData(shippingCourierFormData);
  }

  parseAddress(address: string | null | undefined): [string, string, string] {
    if (!address) {
      return ['', '', ''];
    }

    const cleanedAddress = address.replace(/-/g, '').trim();

    const [streetAndHouseNumber, apartmentNumber = ''] =
      cleanedAddress.split('/');
    const [street, houseNumber] = streetAndHouseNumber
      .trim()
      .split(/\s+(?=\S*$)/);

    return [street.trim(), houseNumber.trim(), apartmentNumber.trim()];
  }

  private getTodayDate(): string {
    const today = new Date();

    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = today.getFullYear();

    return `${year}-${month}-${day}`;
  }
}
